import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { add, format, startOfToday } from "date-fns";
import { es } from "date-fns/locale";
import AllyService from "../../../services/ally.service";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import { AllyReservationItem, AllyReservations } from "../../../types/common.types";
import m, { desktop } from "../../../styles/molanturTheme";
import { Avatar, Card, CountryCode, Icon, IconButton, Label, TourThumb, clp, dateFromIso, longDate, shortDate } from "../ui";
import RangeCalendar from "../ui/RangeCalendar";

// "Mis reservas" del perfil del aliado. Igual que la pestaña Payments de vendedores: al elegir un día
// (o un rango) aparecen solo las reservas que tienen un tour en esas fechas, cada una completa, y
// con "Agregar tour" se le suma otro tour a ESA misma reserva (mismo grupo y mismos pasajeros) en
// vez de volver a cargar a los pasajeros.

export default function Reservations() {
    const { setIsLoading } = useGlobal()
    const { setNewGroup, setNewGroupReservationData } = useAlly()
    const [day, setDay] = useState<Date>(startOfToday())
    const [endDay, setEndDay] = useState<Date | undefined>()
    const [isCalendarOpen, setIsCalendarOpen] = useState(false)
    const [data, setData] = useState<AllyReservations>()
    // Reservas con la lista completa de tours desplegada (en celular solo se ven los del rango).
    const [expanded, setExpanded] = useState<string[]>([])
    const from = format(day, 'yyyy-MM-dd')
    const to = format(endDay || day, 'yyyy-MM-dd')

    const load = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await AllyService.listMyReservations(from, to)
            setData(res.data)
        } catch (error) {
            console.log(error)
            setData(undefined)
        } finally {
            setIsLoading(false)
        }
    }, [from, to, setIsLoading])

    useEffect(() => { load() }, [load])

    function moveDay(days: number) {
        setDay(add(day, { days }))
        setEndDay(undefined)
    }

    // Abre el mismo flujo que "agregar huéspedes" desde el paso 2 (elegir tour y fecha → revisar →
    // voucher), con los pasajeros y el grupo de esta reserva: el tour nuevo queda dentro de ella.
    function addTour(item: AllyReservationItem) {
        if (!item.group_id) return
        setNewGroupReservationData({ group_id: item.group_id })
        setNewGroup(item.passengers)
    }

    // Desglose del resumen: solo lo que cae en las fechas elegidas (igual que data.totals.total).
    const inRange = (data?.items || []).flatMap(item => item.reservations.filter(r => r.in_range))
    const toursTotal = inRange.reduce((acc, r) => acc + r.price, 0)
    const ticketsTotal = inRange.reduce((acc, r) => acc + r.tickets, 0)

    return (
        <Main>
            <header>
                <div>
                    <Label>Historial</Label>
                    <h1>Mis reservas</h1>
                </div>
                <div className="filter">
                    <IconButton name="chevronLeft" label="Día anterior" tone="surface" onClick={() => moveDay(-1)} />
                    <button type="button" className="open-calendar" aria-label="Elegir fechas" aria-expanded={isCalendarOpen} onClick={() => setIsCalendarOpen(!isCalendarOpen)}>
                        <Icon name="calendar" size={16} stroke={2} />
                        <span>{endDay ? `${shortDate(day)} – ${shortDate(endDay)}` : longDate(day)}</span>
                    </button>
                    <IconButton name="chevronRight" label="Día siguiente" tone="surface" onClick={() => moveDay(1)} />
                    {isCalendarOpen && (
                        <RangeCalendar
                            from={day}
                            to={endDay}
                            onChange={(newFrom, newTo) => { setDay(newFrom); setEndDay(newTo) }}
                            onClose={() => setIsCalendarOpen(false)}
                        />
                    )}
                </div>
            </header>

            {data && (
                <div className="totals">
                    <Card $pad={18}><Label>Tours</Label><strong>{data.totals.reservations}</strong></Card>
                    <Card $pad={18}><Label>Pasajeros</Label><strong>{data.totals.passengers}</strong></Card>
                    <Card $pad={18}>
                        <Label>Total (costo + entrada)</Label>
                        <strong>{clp(data.totals.total)}</strong>
                        <span className="split">Tours {clp(toursTotal)} · Entradas {clp(ticketsTotal)}</span>
                    </Card>
                </div>
            )}

            {data && data.items.length === 0 && <Card className="empty">No tienes reservas con tour en estas fechas.</Card>}

            {data && data.items.length > 0 && (
                <div className="list">
                    {data.items.map(item => {
                        const key = item.group_id || item.reservation_id || ''
                        const names = item.passengers.map(p => p.name).join(', ')
                        // Un tour por fila aunque vaya más de un pasajero ("Valle de la Luna · 2 pax").
                        const tours = new Map<string, { date: string, tour: string, pax: number, in_range: boolean }>()
                        item.reservations.forEach(r => {
                            const k = `${r.reservation_date}|${r.tour_id}`
                            const t = tours.get(k) || { date: r.reservation_date, tour: r.tour_id, pax: 0, in_range: r.in_range }
                            t.pax += 1
                            tours.set(k, t)
                        })
                        const rows = [...tours.values()]
                        const outOfRange = rows.filter(t => !t.in_range).length
                        const tourCost = item.reservations.reduce((acc, r) => acc + r.price, 0)
                        const tickets = item.reservations.reduce((acc, r) => acc + r.tickets, 0)
                        const someIncluded = item.reservations.some(r => r.ticket_included)
                        const first = item.first_date_in_range ? dateFromIso(item.first_date_in_range) : undefined
                        const isExpanded = expanded.includes(key)
                        return (
                            <Card key={key} $pad={0} className={`reservation ${isExpanded ? 'expanded' : ''}`} role="group" aria-label={`Reserva de ${names}`}>
                                <div className="cols">
                                    <div className="when">
                                        <span className="day">{first ? format(first, 'd') : ''}</span>
                                        <span className="month">{first ? format(first, 'MMM', { locale: es }) : ''}</span>
                                    </div>
                                    <div className="pax">
                                        <Label>{item.passengers.length} {item.passengers.length === 1 ? 'pasajero' : 'pasajeros'}</Label>
                                        <ul>
                                            {item.passengers.map(p => (
                                                <li key={p.id}>
                                                    <Avatar name={p.name || ''} size={28} />
                                                    <span className="pax-name">{p.name}</span>
                                                    {p.nationality && <CountryCode>{p.nationality}</CountryCode>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="tours">
                                        <Label>Tours de la reserva · {rows.length}</Label>
                                        <ul>
                                            {rows.map(t => (
                                                <li key={`${t.date}${t.tour}`} className={t.in_range ? '' : 'out-of-range'}>
                                                    <span className="tour-date">{format(dateFromIso(t.date), 'EEE d', { locale: es })}</span>
                                                    <TourThumb tourId={t.tour} size={24} />
                                                    <span className="tour-name">{t.tour}{t.pax !== item.passengers.length ? ` · ${t.pax} pax` : ''}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="money">
                                        <strong>{clp(tourCost + tickets)}</strong>
                                        <span>Tours {clp(tourCost)}</span>
                                        <span>{tickets > 0 ? `Entradas ${clp(tickets)}` : someIncluded ? 'Entradas: monto por confirmar' : 'Entradas: pagan en el parque'}</span>
                                    </div>
                                </div>
                                {(outOfRange > 0 || item.group_id) && (
                                    <div className="foot">
                                        {outOfRange > 0 && (
                                            <button type="button" className="more" aria-expanded={isExpanded}
                                                onClick={() => setExpanded(prev => isExpanded ? prev.filter(k => k !== key) : [...prev, key])}>
                                                {isExpanded ? 'Ver menos' : `Ver los ${rows.length} tours`}
                                                <Icon name="chevronDown" size={14} stroke={2.2} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                                            </button>
                                        )}
                                        {item.group_id && (
                                            <button
                                                type="button"
                                                className="add"
                                                title="Agregar otro tour a esta reserva"
                                                aria-label={`Agregar tour a la reserva de ${names}`}
                                                onClick={() => addTour(item)}
                                            >
                                                <Icon name="plus" size={15} stroke={2.4} />Agregar tour
                                            </button>
                                        )}
                                    </div>
                                )}
                            </Card>
                        )
                    })}
                </div>
            )}
        </Main>
    )
}

const Main = styled.div`
    header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
        margin-bottom: 18px;
    }
    h1 {
        margin: 4px 0 0;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 26px;
        line-height: 1.1;
        color: ${m.color.ink};
    }
    .filter {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1 1 280px;
        max-width: 420px;
    }
    .open-calendar {
        flex: 1;
        min-width: 0;
        height: 44px;
        padding: 0 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        border: 1.5px solid ${m.color.lineStrong};
        border-radius: ${m.radius.pill};
        background: ${m.color.surface};
        color: ${m.color.ink};
        font-family: ${m.font.text};
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        &[aria-expanded="true"] { border-color: ${m.color.ink}; }
        span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .totals {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        margin-bottom: 18px;
        > :last-child { grid-column: 1 / -1; }
        strong {
            display: block;
            margin-top: 6px;
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 28px;
            line-height: 1;
            color: ${m.color.ink};
        }
        .split {
            display: block;
            margin-top: 8px;
            font-size: 12.5px;
            color: ${m.color.inkSoft};
        }
    }
    .empty {
        padding: 40px 20px;
        text-align: center;
        font-size: 14px;
        color: ${m.color.inkFaint};
    }
    .list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .reservation { overflow: hidden; }
    .cols {
        display: flex;
        flex-wrap: wrap;
        gap: 14px 18px;
        padding: 16px;
    }
    ul {
        list-style: none;
        margin: 8px 0 0;
        padding: 0;
    }
    .when {
        display: none;
        flex: 0 0 64px;
        align-self: flex-start;
        padding: 10px 0;
        text-align: center;
        border-radius: ${m.radius.md};
        background: ${m.color.surface2};
        .day {
            display: block;
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 26px;
            line-height: 1;
        }
        .month {
            display: block;
            margin-top: 4px;
            font-family: ${m.font.label};
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: ${m.color.inkFaint};
        }
    }
    .pax {
        flex: 1 1 180px;
        min-width: 0;
        li {
            display: flex;
            align-items: center;
            gap: 9px;
            padding: 3px 0;
            font-weight: 600;
            font-size: 13.5px;
        }
    }
    .pax-name {
        flex: 1;
        min-width: 0;
    }
    .tours {
        flex: 2 1 260px;
        min-width: 0;
        li {
            display: flex;
            align-items: center;
            gap: 9px;
            padding: 5px 0;
        }
        /* En celular los tours fuera de las fechas elegidas quedan plegados. */
        li.out-of-range {
            display: none;
            opacity: 0.45;
        }
    }
    .reservation.expanded .tours li.out-of-range { display: flex; }
    .tour-date {
        flex: 0 0 50px;
        font-family: ${m.font.label};
        font-size: 12.5px;
        font-weight: 600;
        color: ${m.color.inkSoft};
    }
    .tour-name {
        flex: 1;
        min-width: 0;
        font-size: 13.5px;
        font-weight: 600;
    }
    .money {
        flex: 1 0 150px;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        text-align: right;
        strong {
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 22px;
            color: ${m.color.ink};
        }
        span {
            font-size: 12px;
            color: ${m.color.inkFaint};
        }
    }
    .foot {
        display: flex;
        border-top: 1px solid ${m.color.line};
        button {
            flex: 1;
            min-height: 46px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            border: none;
            background: transparent;
            cursor: pointer;
            font-family: ${m.font.text};
            font-weight: 600;
            font-size: 13px;
            color: ${m.color.inkSoft};
        }
        .add { color: ${m.color.primaryDeep}; }
        button + button { border-left: 1px solid ${m.color.line}; }
    }

    ${desktop} {
        h1 { font-size: 38px; }
        .totals {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 24px;
            > :last-child { grid-column: auto; }
            strong { font-size: 32px; }
        }
        .cols { padding: 18px; }
        .when { display: block; }
        /* Con espacio, la reserva se muestra completa: lo de otros días va atenuado, no oculto. */
        .tours li.out-of-range { display: flex; }
        .foot { justify-content: flex-end; }
        .foot .more { display: none; }
        .foot .add {
            flex: 0 0 auto;
            padding: 0 20px;
            border-left: none;
        }
    }
`
